import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Eye, EyeOff, AlertCircle, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DEFAULT_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Set defaults if no settings exist
      setSettings({
        risk: {
          base_risk_pct: 0.01,
          max_risk_pct: 0.02,
          min_risk_pct: 0.005,
          wins_to_increase: 2,
          losses_to_decrease: 1,
          pause_after_losses: 3
        },
        symbols: DEFAULT_SYMBOLS,
        strategy_presets: {
          RFS: { lookback: 10, vol_multiplier: 1.5, tp_ratio: 2.0 },
          TRS: { tp_ratio: 2.0 }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Remove any MongoDB-specific fields that shouldn't be sent back
      const settingsToSave = { ...settings };
      delete settingsToSave._id;
      delete settingsToSave.id;
      
      await axios.post(`${API_URL}/api/settings`, settingsToSave);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateRisk = (key, value) => {
    setSettings(prev => ({
      ...prev,
      risk: { ...prev.risk, [key]: value }
    }));
  };

  const updateStrategyPreset = (strategy, key, value) => {
    setSettings(prev => ({
      ...prev,
      strategy_presets: {
        ...prev.strategy_presets,
        [strategy]: { ...prev.strategy_presets[strategy], [key]: value }
      }
    }));
  };

  const addSymbol = () => {
    if (!newSymbol) return;
    const symbol = newSymbol.toUpperCase();
    if (!symbol.endsWith('USDT')) {
      toast.error('Symbol must end with USDT');
      return;
    }
    if (settings.symbols.includes(symbol)) {
      toast.error('Symbol already exists');
      return;
    }
    setSettings(prev => ({
      ...prev,
      symbols: [...prev.symbols, symbol]
    }));
    setNewSymbol('');
    toast.success(`${symbol} added`);
  };

  const removeSymbol = (symbol) => {
    if (settings.symbols.length <= 1) {
      toast.error('Must have at least one symbol');
      return;
    }
    setSettings(prev => ({
      ...prev,
      symbols: prev.symbols.filter(s => s !== symbol)
    }));
    toast.success(`${symbol} removed`);
  };

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <div className="loading-spinner" style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  return (
    <div>
      <header className="page-header">
        <div className="page-header-content">
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Settings</h1>
          <button 
            className="btn btn-primary"
            onClick={saveSettings}
            disabled={saving}
            data-testid="save-settings-button"
          >
            {saving ? (
              <><span className="loading-spinner" style={{ width: '16px', height: '16px' }} /> Saving...</>
            ) : (
              <><Save size={16} /> Save Settings</>
            )}
          </button>
        </div>
      </header>

      <div className="page-container">
        <div className="settings-grid">
          {/* Risk Management */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Risk Management</h3>
            </div>
            <div className="card-content">
              <div className="form-group">
                <label className="form-label">Base Risk (%)</label>
                <input
                  type="number"
                  value={(settings.risk?.base_risk_pct || 0.01) * 100}
                  onChange={(e) => updateRisk('base_risk_pct', parseFloat(e.target.value) / 100)}
                  className="form-input"
                  step="0.1"
                  min="0.1"
                  max="5"
                  data-testid="settings-base-risk"
                />
                <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginTop: '0.25rem' }}>
                  Default risk per trade (1% recommended)
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Max Risk (%)</label>
                <input
                  type="number"
                  value={(settings.risk?.max_risk_pct || 0.02) * 100}
                  onChange={(e) => updateRisk('max_risk_pct', parseFloat(e.target.value) / 100)}
                  className="form-input"
                  step="0.1"
                  min="0.5"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Min Risk (%)</label>
                <input
                  type="number"
                  value={(settings.risk?.min_risk_pct || 0.005) * 100}
                  onChange={(e) => updateRisk('min_risk_pct', parseFloat(e.target.value) / 100)}
                  className="form-input"
                  step="0.1"
                  min="0.1"
                  max="2"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Wins to Increase Risk</label>
                <input
                  type="number"
                  value={settings.risk?.wins_to_increase || 2}
                  onChange={(e) => updateRisk('wins_to_increase', parseInt(e.target.value))}
                  className="form-input"
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Losses to Decrease Risk</label>
                <input
                  type="number"
                  value={settings.risk?.losses_to_decrease || 1}
                  onChange={(e) => updateRisk('losses_to_decrease', parseInt(e.target.value))}
                  className="form-input"
                  min="1"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Pause After Consecutive Losses</label>
                <input
                  type="number"
                  value={settings.risk?.pause_after_losses || 3}
                  onChange={(e) => updateRisk('pause_after_losses', parseInt(e.target.value))}
                  className="form-input"
                  min="2"
                  max="10"
                />
                <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginTop: '0.25rem' }}>
                  Pause trading for 24h after this many losses
                </p>
              </div>
            </div>
          </div>

          {/* Trading Pairs */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Trading Pairs</h3>
            </div>
            <div className="card-content">
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={newSymbol}
                  onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g., XRPUSDT"
                  className="form-input"
                  style={{ flex: 1 }}
                  data-testid="add-symbol-input"
                />
                <button 
                  className="btn btn-primary"
                  onClick={addSymbol}
                  data-testid="add-symbol-button"
                >
                  <Plus size={16} /> Add
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {settings.symbols?.map(symbol => (
                  <div 
                    key={symbol}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      backgroundColor: '#0F1318',
                      border: '1px solid #1E2329',
                      borderRadius: '8px'
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{symbol}</span>
                    <button
                      onClick={() => removeSymbol(symbol)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#9AA3AF',
                        cursor: 'pointer',
                        padding: '2px'
                      }}
                      data-testid={`remove-symbol-${symbol}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RFS Strategy Presets */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">RFS Strategy Presets</h3>
            </div>
            <div className="card-content">
              <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginBottom: '1rem' }}>
                Ruptura Fractal Sincronizada - Breakout with volume confirmation
              </p>

              <div className="form-group">
                <label className="form-label">Consolidation Lookback</label>
                <input
                  type="number"
                  value={settings.strategy_presets?.RFS?.lookback || 10}
                  onChange={(e) => updateStrategyPreset('RFS', 'lookback', parseInt(e.target.value))}
                  className="form-input"
                  min="5"
                  max="30"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Volume Multiplier</label>
                <input
                  type="number"
                  value={settings.strategy_presets?.RFS?.vol_multiplier || 1.5}
                  onChange={(e) => updateStrategyPreset('RFS', 'vol_multiplier', parseFloat(e.target.value))}
                  className="form-input"
                  step="0.1"
                  min="1"
                  max="5"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Take Profit Ratio</label>
                <input
                  type="number"
                  value={settings.strategy_presets?.RFS?.tp_ratio || 2.0}
                  onChange={(e) => updateStrategyPreset('RFS', 'tp_ratio', parseFloat(e.target.value))}
                  className="form-input"
                  step="0.5"
                  min="1"
                  max="5"
                />
              </div>
            </div>
          </div>

          {/* TRS Strategy Presets */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">TRS Strategy Presets</h3>
            </div>
            <div className="card-content">
              <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginBottom: '1rem' }}>
                Resorte Mecánico (Trend Spring) - EMA pullback with stochastic
              </p>

              <div className="form-group">
                <label className="form-label">Take Profit Ratio</label>
                <input
                  type="number"
                  value={settings.strategy_presets?.TRS?.tp_ratio || 2.0}
                  onChange={(e) => updateStrategyPreset('TRS', 'tp_ratio', parseFloat(e.target.value))}
                  className="form-input"
                  step="0.5"
                  min="1"
                  max="5"
                />
              </div>

              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#0F1318', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: '#9AA3AF' }}>
                  <strong>TRS Indicators:</strong>
                  <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                    <li>EMA 200 (Trend Filter)</li>
                    <li>EMA 20 (Entry Zone)</li>
                    <li>Stochastic (14, 3, 3)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* API Keys */}
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header">
              <h3 className="card-title">API Configuration</h3>
            </div>
            <div className="card-content">
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '0.75rem', 
                padding: '1rem', 
                backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <AlertCircle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '0.875rem' }}>
                  <strong style={{ color: '#F59E0B' }}>Security Note:</strong>
                  <p style={{ color: '#9AA3AF', margin: '0.25rem 0 0' }}>
                    API keys are stored securely in the backend .env file. To update them, edit the .env file directly on your server.
                    Never share your API keys or commit them to version control.
                  </p>
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Binance API Key</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value="••••••••••••••••••••"
                      readOnly
                      className="form-input"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: '#9AA3AF',
                        cursor: 'pointer'
                      }}
                    >
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    padding: '0.625rem 0.875rem',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <Check size={18} style={{ color: '#22C55E' }} />
                    <span style={{ color: '#22C55E' }}>Read-Only Access Configured</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#9AA3AF', marginTop: '0.25rem' }}>
                    Enable Spot Trading permission when you have your VPS IP
                  </p>
                </div>
              </div>

              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#0F1318', borderRadius: '8px' }}>
                <p style={{ fontSize: '0.75rem', color: '#9AA3AF', margin: 0 }}>
                  <strong>Recommended VPS Locations:</strong> Singapore, Japan, Hong Kong, Australia, Brazil
                  <br />
                  <strong>Avoid:</strong> USA, Canada, Netherlands, UK (Binance restricted)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
